import React, { useState, useCallback } from "react";

import type { ProductVariant, ServerDataSuccess } from "./route";

import { Button, FindInStore, IconMap } from "@bared/ui";
import { Sheet } from "~/components/Sheet";
import { useRichText } from "@hooks/useRichText";
export interface FindInStoreSheetProps {
  selectedVariant: Pick<
    ProductVariant,
    "id" | "status" | "storeAvailability"
  > | null;
  /** Country code to use for the store availability */
  countryCode?: string;
  /** Whether to show the variant selector error */
  setVariantSelectorError: () => void;
  /** Sheet footer content to use for all sheets */
  sheetFooterContent: ServerDataSuccess["template"]["sheetFooterContent"];
}

export function FindInStoreSheet(props: FindInStoreSheetProps) {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const { parseContent } = useRichText();
  const footerContent = parseContent(props.sheetFooterContent);

  const handleSheetToggle = useCallback(() => {
    if (!props.selectedVariant) {
      props.setVariantSelectorError();
      return;
    }
    setIsOpen((prev) => !prev);
  }, [props.setVariantSelectorError, props.selectedVariant, isOpen]);

  return (
    <>
      <div>
        <Button
          variant="link"
          onClick={handleSheetToggle}
          className="flex items-baseline gap-nano font-medium"
        >
          <IconMap className="w-mini text-primary hover:text-secondary" />
          Find In-Store
        </Button>
      </div>

      <Sheet
        isOpen={isOpen}
        onClose={handleSheetToggle}
        title="Find In-Store"
        className="p-midi md:p-maxi"
      >
        {props.selectedVariant && (
          <FindInStore
            countryCode={props.countryCode ?? undefined}
            storeAvailability={
              props.selectedVariant?.storeAvailability?.map(
                (storeAvailability) => ({
                  ...storeAvailability,
                  location: {
                    ...storeAvailability.location,
                    metafields: [],
                    storeDirectionLink:
                      storeAvailability.location?.storeDirectionLink ??
                      undefined,
                    address: {
                      address1:
                        storeAvailability.location.address.address1 ?? "",
                      address2:
                        storeAvailability.location.address.address2 ?? "",
                      city: storeAvailability.location.address.city ?? "",
                      provinceCode:
                        storeAvailability.location.address.provinceCode ?? "",
                      zip: storeAvailability.location.address.zip ?? "",
                      phone: storeAvailability.location.address.phone ?? "",
                      countryCode:
                        storeAvailability.location.address.countryCode ?? "",
                      formatted: [
                        storeAvailability.location.address.address1 ?? "",
                        storeAvailability.location.address.city ?? "",
                        storeAvailability.location.address.provinceCode ?? "",
                        storeAvailability.location.address.zip ?? "",
                      ].filter(Boolean),
                    },
                  },
                }),
              ) ?? []
            }
          />
        )}
        {footerContent && (
          <div className="mt-midi border-t border-primary-300 pt-midi">
            {footerContent}
          </div>
        )}
      </Sheet>
    </>
  );
}
